import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntdApp } from 'antd';
import { AuthProvider } from './contexts/AuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import MainLayout from './components/Layout/MainLayout';
import Dashboard from './components/Dashboard/Dashboard';
import CustomerList from './components/CustomerList';
import CustomerForm from './components/CustomerForm';
import CustomerDetail from './components/CustomerDetail';
import VehicleForm from './components/VehicleForm';
import WorkOrderList from './components/WorkOrder/WorkOrderList';
import ArchivedWorkOrders from './components/WorkOrder/ArchivedWorkOrders';
import WorkOrderForm from './components/WorkOrder/WorkOrderForm';
import OptimizedWorkOrderDetail from './components/WorkOrder/OptimizedWorkOrderDetail';
import EstimateList from './components/EstimateList';
import EstimateForm from './components/EstimateForm';
import EstimateDetail from './components/EstimateDetail';
import TemplateManagement from './components/Templates/TemplateManagement';
import Settings from './components/Settings/Settings';
import { AppointmentCalendar, DayView, TechScheduleView } from './components/Appointments';
import { SchedulingHub } from './components/SchedulingHub';
import { TechnicianScheduleSettings } from './components/Settings/TechnicianScheduleSettings';
import { BookingSettings } from './components/Settings/BookingSettings';
import LandingPage from './pages/LandingPage';
import Login from './components/auth/Login';
import PrivateRoute from './components/auth/PrivateRoute';
import CustomerPrivateRoute from './components/auth/CustomerPrivateRoute';
import UserManagement from './components/admin/UserManagement';
import OwnerDashboard from './components/dashboards/OwnerDashboard';
import AdvisorDashboard from './components/dashboards/AdvisorDashboard';
import TechnicianDashboard from './components/dashboards/TechnicianDashboard';
import CustomerPortalDashboard from './components/dashboards/CustomerPortalDashboard';
import PublicEstimateView from './pages/PublicEstimateView';
import PublicWorkOrderView from './pages/PublicWorkOrderView';
import PaymentReturn from './pages/PaymentReturn';
import EstimateTemplatesPage from './pages/EstimateTemplatesPage';
import ServiceCategoryManagerPage from './pages/owner/ServiceCategoryManagerPage';
import OwnerHub from './pages/owner/OwnerHub';
import QuoteRequestsPage from './pages/QuoteRequestsPage';
import RewardSettings from './components/admin/RewardSettings';
import MarketingDashboard from './components/admin/MarketingDashboard';
import SharedPartsBrowser from './pages/admin/SharedPartsBrowser';
import { BookingPortal } from './components/Public/BookingPortal';
import CustomerLoginPage from './pages/CustomerLoginPage';
import CustomerDashboard from './pages/CustomerDashboard';
import CustomerVehicles from './pages/CustomerVehicles';
import CustomerRewards from './pages/CustomerRewards';
import CustomerPreferences from './pages/CustomerPreferences';
import CustomerProfile from './pages/CustomerProfile';
import CustomerNotifications from './pages/CustomerNotifications';
import { logger } from './utils/logger';
import './App.css';
import './styles/designSystem.css';

const App: React.FC = () => {
  useEffect(() => {
    // PWA functionality is initialized in index.tsx for production builds
    logger.log('[App] Component mounted');

    // Viewport is configured in index.html — no dynamic override

    // Add mobile-specific event listeners
    const handleOnline = () => {
      logger.log('[App] Network back online');
      document.body.classList.remove('offline-mode');
    };

    const handleOffline = () => {
      logger.log('[App] Network went offline');
      document.body.classList.add('offline-mode');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Set initial offline state
    if (!navigator.onLine) {
      document.body.classList.add('offline-mode');
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#2563eb',
          colorPrimaryHover: '#1d4ed8',
          colorPrimaryActive: '#1e40af',
          borderRadius: 8,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: 14,
          controlHeight: 44,
          colorBgLayout: '#f9fafb',
          colorBgContainer: '#ffffff',
          colorBorder: '#e5e7eb',
          colorText: '#111827',
          colorTextSecondary: '#6b7280',
        },
        components: {
          Button: {
            borderRadius: 8,
            controlHeight: 44,
            fontWeight: 500,
          },
          Card: {
            borderRadius: 12,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          },
          Input: {
            borderRadius: 8,
            controlHeight: 44,
          },
          Select: {
            borderRadius: 8,
            controlHeight: 44,
          },
        },
      }}
    >
      <AntdApp>
        <AuthProvider>
          <CustomerAuthProvider>
            <NotificationProvider>
              <Router>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/services" element={<Navigate to="/book" replace />} />
                  <Route path="/book" element={<BookingPortal />} />
                  <Route path="/booking" element={<BookingPortal />} />
                  <Route path="/estimate/:token" element={<PublicEstimateView />} />
                  <Route path="/work-order/:token" element={<PublicWorkOrderView />} />
                  <Route path="/payment/return" element={<PaymentReturn />} />
                  <Route path="/payment/success" element={<PaymentReturn />} />
                  <Route path="/payment/canceled" element={<Navigate to="/" replace />} />

                  {/* Customer Portal Routes */}
                  <Route path="/customer/login" element={<CustomerLoginPage />} />
                  <Route path="/customer/dashboard" element={<CustomerPrivateRoute><CustomerDashboard /></CustomerPrivateRoute>} />
                  <Route path="/customer/vehicles" element={<CustomerPrivateRoute><CustomerVehicles /></CustomerPrivateRoute>} />
                  <Route path="/customer/rewards" element={<CustomerPrivateRoute><CustomerRewards /></CustomerPrivateRoute>} />
                  <Route path="/customer/preferences" element={<CustomerPrivateRoute><CustomerPreferences /></CustomerPrivateRoute>} />
                  <Route path="/customer/profile" element={<CustomerPrivateRoute><CustomerProfile /></CustomerPrivateRoute>} />
                  <Route path="/customer/notifications" element={<CustomerPrivateRoute><CustomerNotifications /></CustomerPrivateRoute>} />
                  <Route path="/customer/estimates" element={<CustomerPrivateRoute><CustomerDashboard /></CustomerPrivateRoute>} />
                  <Route path="/customer/history" element={<CustomerPrivateRoute><CustomerDashboard /></CustomerPrivateRoute>} />

                  {/* Legacy route redirect */}
                  <Route path="/admin/login" element={<Navigate to="/login" replace />} />

                  {/* Owner Routes */}
                  <Route path="/owner/*" element={
                    <PrivateRoute allowedRoles={['owner']}>
                      <MainLayout>
                        <Routes>
                          <Route path="/dashboard" element={<OwnerDashboard />} />
                          <Route path="/users" element={<UserManagement />} />
                          <Route path="/customers" element={<CustomerList />} />
                          <Route path="/customers/new" element={<CustomerForm />} />
                          <Route path="/customers/:id" element={<CustomerDetail />} />
                          <Route path="/customers/:id/edit" element={<CustomerForm />} />
                          <Route path="/estimates" element={<EstimateList />} />
                          <Route path="/estimates/new" element={<EstimateForm />} />
                          <Route path="/estimates/:id" element={<EstimateDetail />} />
                          <Route path="/estimates/:id/edit" element={<EstimateForm />} />
                          <Route path="/work-orders" element={<WorkOrderList />} />
                          <Route path="/work-orders/archived" element={<ArchivedWorkOrders />} />
                          <Route path="/work-orders/:id" element={<OptimizedWorkOrderDetail />} />
                          <Route path="/appointments" element={<SchedulingHub />} />
                          <Route path="/appointments/legacy" element={<AppointmentCalendar />} />
                          <Route path="/appointments/day" element={<DayView />} />
                          <Route path="/appointments/schedule" element={<TechScheduleView />} />
                          <Route path="/templates" element={<TemplateManagement />} />
                          <Route path="/estimate-templates" element={<EstimateTemplatesPage />} />
                          <Route path="/quote-requests" element={<QuoteRequestsPage />} />
                          <Route path="/work-orders/new" element={<WorkOrderForm />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/settings/technician-schedules" element={<TechnicianScheduleSettings />} />
                          <Route path="/settings/booking-settings" element={<BookingSettings />} />
                          <Route path="/settings/rewards" element={<RewardSettings />} />
                          <Route path="/shared-parts" element={<SharedPartsBrowser />} />
                          <Route path="/marketing" element={<MarketingDashboard />} />
                          <Route path="/reports" element={<OwnerHub />} />
                          <Route path="/service-categories" element={<ServiceCategoryManagerPage />} />
                        </Routes>
                      </MainLayout>
                    </PrivateRoute>
                  } />

                  {/* Advisor Routes */}
                  <Route path="/advisor/*" element={
                    <PrivateRoute allowedRoles={['owner', 'advisor']}>
                      <MainLayout>
                        <Routes>
                          <Route path="/dashboard" element={<AdvisorDashboard />} />
                          <Route path="/customers" element={<CustomerList />} />
                          <Route path="/customers/new" element={<CustomerForm />} />
                          <Route path="/customers/:id" element={<CustomerDetail />} />
                          <Route path="/estimates" element={<EstimateList />} />
                          <Route path="/estimates/new" element={<EstimateForm />} />
                          <Route path="/estimates/:id" element={<EstimateDetail />} />
                          <Route path="/estimates/:id/edit" element={<EstimateForm />} />
                          <Route path="/work-orders" element={<WorkOrderList />} />
                          <Route path="/work-orders/archived" element={<ArchivedWorkOrders />} />
                          <Route path="/work-orders/:id" element={<OptimizedWorkOrderDetail />} />
                          <Route path="/appointments" element={<SchedulingHub />} />
                          <Route path="/appointments/legacy" element={<AppointmentCalendar />} />
                          <Route path="/appointments/day" element={<DayView />} />
                          <Route path="/appointments/schedule" element={<TechScheduleView />} />
                          <Route path="/templates" element={<TemplateManagement />} />
                          <Route path="/estimate-templates" element={<EstimateTemplatesPage />} />
                          <Route path="/quote-requests" element={<QuoteRequestsPage />} />
                          <Route path="/shared-parts" element={<SharedPartsBrowser />} />
                        </Routes>
                      </MainLayout>
                    </PrivateRoute>
                  } />

                  {/* Technician Routes */}
                  <Route path="/technician/*" element={
                    <PrivateRoute allowedRoles={['owner', 'advisor', 'technician']}>
                      <MainLayout>
                        <Routes>
                          <Route path="/dashboard" element={<TechnicianDashboard />} />
                          <Route path="/work-orders" element={<WorkOrderList />} />
                          <Route path="/work-orders/:id" element={<OptimizedWorkOrderDetail />} />
                          <Route path="/schedule" element={<TechScheduleView />} />
                          <Route path="/appointments" element={<TechScheduleView />} />
                          <Route path="/appointments/day" element={<DayView />} />
                        </Routes>
                      </MainLayout>
                    </PrivateRoute>
                  } />

                  {/* Customer Portal Routes */}
                  <Route path="/portal/*" element={
                    <PrivateRoute allowedRoles={['customer']}>
                      <MainLayout>
                        <Routes>
                          <Route path="/dashboard" element={<CustomerPortalDashboard />} />
                          <Route path="/estimates" element={<div>My Estimates - Coming Soon</div>} />
                          <Route path="/vehicles" element={<div>My Vehicles - Coming Soon</div>} />
                          <Route path="/history" element={<div>Service History - Coming Soon</div>} />
                          <Route path="/payments" element={<div>Payments - Coming Soon</div>} />
                        </Routes>
                      </MainLayout>
                    </PrivateRoute>
                  } />

                  {/* Legacy /admin routes - redirect to appropriate role-based dashboard */}
                  <Route path="/admin/*" element={
                    <PrivateRoute>
                      <MainLayout>
                        <Routes>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/customers" element={<CustomerList />} />
                          <Route path="/customers/new" element={<CustomerForm />} />
                          <Route path="/customers/:id" element={<CustomerDetail />} />
                          <Route path="/customers/:id/edit" element={<CustomerForm />} />
                          <Route path="/customers/:id/vehicles/new" element={<VehicleForm />} />
                          <Route path="/customers/:id/vehicles/:vehicleId/edit" element={<VehicleForm />} />
                          <Route path="/work-orders" element={<WorkOrderList />} />
                          <Route path="/work-orders/new" element={<WorkOrderForm />} />
                          <Route path="/work-orders/:id" element={<OptimizedWorkOrderDetail />} />
                          <Route path="/estimates" element={<EstimateList />} />
                          <Route path="/estimates/new" element={<EstimateForm />} />
                          <Route path="/estimates/:id" element={<EstimateDetail />} />
                          <Route path="/estimates/:id/edit" element={<EstimateForm />} />
                          <Route path="/appointments" element={<SchedulingHub />} />
                          <Route path="/appointments/legacy" element={<AppointmentCalendar />} />
                          <Route path="/appointments/day" element={<DayView />} />
                          <Route path="/appointments/schedule" element={<TechScheduleView />} />
                          <Route path="/templates" element={<TemplateManagement />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/settings/technician-schedules" element={<TechnicianScheduleSettings />} />
                          <Route path="/settings/booking-settings" element={<BookingSettings />} />
                        </Routes>
                      </MainLayout>
                    </PrivateRoute>
                  } />
                </Routes>
              </Router>
            </NotificationProvider>
          </CustomerAuthProvider>
        </AuthProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
